// /src/GuitarSimulator/FrequencySpectrumDisplay.js

import React, { useRef, useEffect, useState } from 'react';
import './FrequencySpectrumDisplay.css';

const FrequencySpectrumDisplay = ({ 
  audioAnalyzer, 
  isActive, 
  language,
  height = 200,
  showGuitarFrequencies = true 
}) => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [displayMode, setDisplayMode] = useState('spectrum'); // 'spectrum', 'waterfall', 'notes'

  // 기타 주파수 정의 (음표별)
  const guitarFrequencies = {
    'E2': 82.41, 'F2': 87.31, 'F#2': 92.50, 'G2': 98.00, 'G#2': 103.83, 
    'A2': 110.00, 'A#2': 116.54, 'B2': 123.47, 'C3': 130.81, 'C#3': 138.59, 
    'D3': 146.83, 'D#3': 155.56, 'E3': 164.81, 'F3': 174.61, 'F#3': 185.00, 
    'G3': 196.00, 'G#3': 207.65, 'A3': 220.00, 'A#3': 233.08, 'B3': 246.94,
    'C4': 261.63, 'C#4': 277.18, 'D4': 293.66, 'D#4': 311.13, 'E4': 329.63, 
    'F4': 349.23, 'F#4': 369.99, 'G4': 392.00, 'G#4': 415.30, 'A4': 440.00, 
    'A#4': 466.16, 'B4': 493.88, 'C5': 523.25, 'C#5': 554.37, 'D5': 587.33,
    'D#5': 622.25, 'E5': 659.25, 'F5': 698.46, 'F#5': 739.99, 'G5': 783.99
  };

  // 주파수를 로그 스케일로 변환
  const frequencyToX = (frequency, canvasWidth) => {
    const minFreq = 80;
    const maxFreq = 800;
    const logMin = Math.log10(minFreq);
    const logMax = Math.log10(maxFreq);
    const logFreq = Math.log10(Math.max(minFreq, Math.min(maxFreq, frequency)));
    return ((logFreq - logMin) / (logMax - logMin)) * canvasWidth;
  };

  // 스펙트럼 그리기
  const drawSpectrum = (ctx, canvas) => {
    if (!audioAnalyzer || !audioAnalyzer.analyser || !audioAnalyzer.dataArray) return;

    audioAnalyzer.analyser.getByteFrequencyData(audioAnalyzer.dataArray);
    
    const width = canvas.width;
    const height = canvas.height;
    const bufferLength = audioAnalyzer.bufferLength;
    const sampleRate = audioAnalyzer.audioContext.sampleRate;
    const frequencyResolution = sampleRate / (audioAnalyzer.analyser.fftSize * 2);

    // 배경 그리기
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(0, 0, width, height);

    // 그리드 그리기
    drawGrid(ctx, width, height);

    // 스펙트럼 그리기
    ctx.beginPath();
    ctx.strokeStyle = '#00ff88';
    ctx.lineWidth = 2;

    let x = 0;
    for (let i = 0; i < bufferLength; i++) {
      const frequency = i * frequencyResolution;
      if (frequency < 80 || frequency > 800) continue;

      x = frequencyToX(frequency, width);
      const barHeight = (audioAnalyzer.dataArray[i] / 255) * height;
      
      if (i === 0) {
        ctx.moveTo(x, height - barHeight);
      } else {
        ctx.lineTo(x, height - barHeight);
      }
    }
    ctx.stroke();

    // 기타 주파수 마커 그리기
    if (showGuitarFrequencies) {
      drawGuitarFrequencyMarkers(ctx, width, height);
    }

    // 피크 주파수 표시
    drawPeakFrequencies(ctx, width, height, frequencyResolution);
  };

  // 음표별 강도 표시
  const drawNoteIntensities = (ctx, canvas) => {
    if (!audioAnalyzer) return;

    const width = canvas.width;
    const height = canvas.height;
    
    // 배경
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, width, height);

    const noteIntensities = audioAnalyzer.analyzeFrequencies();
    const notes = Object.keys(guitarFrequencies);
    const barWidth = width / notes.length;

    notes.forEach((note, index) => {
      const intensity = noteIntensities[note] || 0;
      const x = index * barWidth;
      const barHeight = intensity * height;

      // 강도별 색상
      let color;
      if (intensity < 0.3) {
        color = `hsl(240, ${40 + intensity * 100}%, ${60 + intensity * 20}%)`;
      } else if (intensity < 0.6) {
        color = `hsl(${180 - (intensity - 0.3) * 400}, ${70 + intensity * 20}%, ${70 - intensity * 10}%)`;
      } else {
        color = `hsl(${60 - (intensity - 0.6) * 150}, ${90 + intensity * 10}%, ${60 - intensity * 10}%)`;
      }

      // 막대 그리기
      ctx.fillStyle = color;
      ctx.fillRect(x, height - barHeight, barWidth - 2, barHeight);

      // 음표 이름 표시
      if (intensity > 0.1) {
        ctx.fillStyle = '#ffffff';
        ctx.font = '12px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(note, x + barWidth / 2, height - barHeight - 5);
      }
    });
  };

  // 그리드 그리기
  const drawGrid = (ctx, width, height) => {
    ctx.strokeStyle = '#333333';
    ctx.lineWidth = 1;

    // 주파수 그리드 (로그 스케일)
    const frequencies = [100, 200, 300, 400, 500, 600, 700, 800];
    frequencies.forEach(freq => {
      const x = frequencyToX(freq, width);
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();

      // 주파수 라벨
      ctx.fillStyle = '#666666';
      ctx.font = '10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`${freq}Hz`, x, height - 5);
    });

    // 강도 그리드
    for (let i = 1; i < 5; i++) {
      const y = (height / 5) * i;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  };

  // 기타 주파수 마커
  const drawGuitarFrequencyMarkers = (ctx, width, height) => {
    Object.entries(guitarFrequencies).forEach(([note, frequency]) => {
      const x = frequencyToX(frequency, width);
      
      // 마커 선
      ctx.strokeStyle = '#ff6b6b';
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 2]);
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
      ctx.setLineDash([]);

      // 음표 라벨
      ctx.fillStyle = '#ff6b6b';
      ctx.font = '9px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(note, x, 12);
    });
  };

  // 피크 주파수 표시
  const drawPeakFrequencies = (ctx, width, height, frequencyResolution) => {
    const peaks = [];
    const threshold = 100;

    // 피크 찾기
    for (let i = 1; i < audioAnalyzer.bufferLength - 1; i++) {
      const current = audioAnalyzer.dataArray[i];
      const prev = audioAnalyzer.dataArray[i - 1];
      const next = audioAnalyzer.dataArray[i + 1];

      if (current > threshold && current > prev && current > next) {
        const frequency = i * frequencyResolution;
        if (frequency >= 80 && frequency <= 800) {
          peaks.push({ frequency, amplitude: current });
        }
      }
    }

    // 상위 5개 피크 표시
    peaks.sort((a, b) => b.amplitude - a.amplitude);
    peaks.slice(0, 5).forEach((peak, index) => {
      const x = frequencyToX(peak.frequency, width);
      const y = height - (peak.amplitude / 255) * height;

      ctx.fillStyle = '#ffff00';
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#ffff00';
      ctx.font = '11px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`${Math.round(peak.frequency)}Hz`, x, y - 8);
    });
  };

  // 애니메이션 루프
  const animate = () => {
    if (!isActive || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (displayMode === 'spectrum') {
      drawSpectrum(ctx, canvas);
    } else if (displayMode === 'notes') {
      drawNoteIntensities(ctx, canvas);
    }

    animationFrameRef.current = requestAnimationFrame(animate);
  };

  // 캔버스 초기화
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = canvas.offsetWidth * window.devicePixelRatio;
    canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    const ctx = canvas.getContext('2d');
    // ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
  }, []);

  // 애니메이션 시작/중지
  useEffect(() => {
    if (isActive && audioAnalyzer) {
      animate();
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isActive, audioAnalyzer, displayMode]);

  return (
    <div className="frequency-spectrum-display">
      <div className="spectrum-header">
        <h3>
          {language === 'KR' ? '주파수 스펙트럼' : 'Frequency Spectrum'}
        </h3>
        
        <div className="display-mode-buttons">
          <button
            onClick={() => setDisplayMode('spectrum')}
            className={`mode-btn ${displayMode === 'spectrum' ? 'active' : ''}`}
          >
            {language === 'KR' ? '스펙트럼' : 'Spectrum'}
          </button>
          <button
            onClick={() => setDisplayMode('notes')}
            className={`mode-btn ${displayMode === 'notes' ? 'active' : ''}`}
          >
            {language === 'KR' ? '음표별' : 'Notes'}
          </button>
        </div>
      </div>

      <canvas
        ref={canvasRef}
        className="spectrum-canvas"
        style={{ height: `${height}px` }}
      />

      <div className="spectrum-info">
        <small>
          {displayMode === 'spectrum' 
            ? (language === 'KR' 
              ? '• 빨간 선: 기타 음표 주파수 | 노란 점: 피크 주파수'
              : '• Red lines: Guitar note frequencies | Yellow dots: Peak frequencies')
            : (language === 'KR'
              ? '• 막대 높이: 음표 강도 | 색상: 파란색(약함) → 빨간색(강함)'
              : '• Bar height: Note intensity | Color: Blue(weak) → Red(strong)')
          }
        </small>
      </div>
    </div>
  );
};

export default FrequencySpectrumDisplay;